/**
 * ScenarioTypeSelectorModal — v10.7.0 Fase 1
 * Modal que aparece al presionar "Nuevo Escenario" y pregunta el tipo:
 * Clínico / Habilidades Blandas / Híbrido.
 *
 * Los tipos avanzados aparecen deshabilitados si el super_admin no los
 * ha activado vía feature flags (`/api/scenarios/feature-flags`).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Pulse, ChatCircle, StackSimple, X, Info } from '@phosphor-icons/react';
import api from '../../api';

const ICONS = {
  clinical: Pulse,
  soft_skills: ChatCircle,
  hybrid: StackSimple,
};

const TYPE_TESTID = {
  clinical: 'scenario-type-clinical',
  soft_skills: 'scenario-type-soft_skills',
  hybrid: 'scenario-type-hybrid',
};

export default function ScenarioTypeSelectorModal({ open, onClose }) {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/scenarios/types')
      .then((r) => setTypes(r.data.types || []))
      .catch(() => toast.error('No se pudo cargar la lista de tipos'))
      .finally(() => setLoading(false));
  }, [open]);

  const pick = (t) => {
    if (!t.enabled) {
      toast.info(`${t.label} aún no está habilitado en esta plataforma. Un super_admin puede activarlo en Ajustes → Feature Flags.`);
      return;
    }
    // Fase 1: solo `clinical` tiene editor completo funcionando.
    // Los tipos avanzados se comportan como el clínico hasta que existan sus editores dedicados (Fase 2/3).
    navigate(`/app/admin/scenarios/new?type=${t.value}`);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="scenario-type-selector"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              ¿Qué tipo de escenario deseas crear?
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Cada tipo abre un editor distinto adaptado a su propósito.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="close-type-selector"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
              Cargando tipos…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {types.map((t) => {
                const Icon = ICONS[t.value] || Pulse;
                return (
                  <button
                    key={t.value}
                    onClick={() => pick(t)}
                    disabled={!t.enabled}
                    data-testid={TYPE_TESTID[t.value]}
                    className={`text-left p-5 rounded-xl border-2 transition-all group relative ${
                      t.enabled
                        ? 'border-slate-200 hover:border-cyan-500 hover:shadow-md hover:-translate-y-0.5 cursor-pointer bg-white'
                        : 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${t.color}15`, color: t.color }}
                    >
                      <Icon size={22} weight="duotone" />
                    </div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="text-base font-semibold text-slate-900">{t.label}</h3>
                      {!t.enabled && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">
                          Beta
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {t.description}
                    </p>
                    {!t.enabled && (
                      <p className="mt-3 text-[11px] text-slate-500 flex items-start gap-1.5">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        Aún no habilitado. Actívalo en Ajustes → Feature Flags.
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
